import React, { useContext, useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { PDFViewer, Document, Page, Text, View, StyleSheet, pdf, Image, Link } from '@react-pdf/renderer';
import farmpactLogo from '../../images/farmpact-logo.jpg';
import $data from '../../services/$data';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Checkbox,
  ListItemIcon,
} from '@mui/material';
import { AppContext } from '../../AppContext';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: '60px 20px',
  },
  section: {
    padding: 20,
  },
});

const EmissionReport = ({ open, year, parcels = [], photos = [], project, onClose }) => {
  const { selected } = useContext(AppContext);
  const [loading, setLoading] = useState({
    assessments: true,
    reports: true,
    documents: true,
    submit: false
  });

  const [assessments, setAssessments] = useState([]);
  const [reports, setReports] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedParcels, setSelected] = useState([]);
  const getBlob = async () => {
    setLoading(true);
    try {
      let blob = await pdf(renderContent()).toBlob();
      let file = new File([blob], 'emission-report.pdf', { type: 'application/pdf' });
      await $data.uploadReport(file, null, 'emission_report' + '_' + year, selected);
      onClose(true);
    } catch (error) {
      console.error('Error generating PDF blob:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEmissionAssessments = async () => {
    setLoading(current => ({ ...current, assessments: true }));
    try {
      let data = await $data.getCCRecords(project?.id);
      setAssessments(data)
      setLoading(current => ({ ...current, assessments: false }));
    } catch (error) {
      console.error("Error fetching assessments:", error);
      setLoading(current => ({ ...current, assessments: false }));
    }
  };

  const getReports = async () => {
    setLoading(current => ({ ...current, reports: true }));
    try {
      let data = await $data.getReports();
      setReports(data.results);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(current => ({ ...current, reports: false }));
    }
  }

  const getDocuments = async () => {
    setLoading(current => ({ ...current, documents: true }));
    try {
      let files = await $data.getDocuments();
      setDocuments(files);
    } catch (e) {
      throw e;
    } finally {
      setLoading(current => ({ ...current, documents: false }));
    }
  }


  const compatibilityCheck = (parcel) => {
    // IS APPLICABLE AND HAS GENERATED REPORT
    if (!parcel.applicabilityStatus === 'Approved' || !Boolean(reports.find((report) => report.parcel === parcel.id && report.tag === 'applicability_report'))) {
      return false;
    }

    // HAS EMISSIONS ASSESSMENT FOR THE SELECTED YEAR
    if (!Boolean(assessments.find((assessment) => assessment.parcel_id === parcel.id && assessment.year.toString() === year.toString()))) {
      return false;
    }

    return true;
  }

  useEffect(() => {
    getEmissionAssessments();
    getReports();
    getDocuments();
  }, [])

  const renderHeader = () => {
    return (
      <>
        <View style={{ ...styles.section, alignItems: 'center', marginTop: '20px' }}>
          <Image src={farmpactLogo} style={{ width: '200px' }} />
          <Text style={{ marginTop: '20px' }}>Report on GHG Emission Reductions</Text>
        </View>
        <View style={{ padding: 20, paddingBottom: '10px' }}>
          <Text style={{ fontSize: '14px' }}>Farm: <Text style={{ fontWeight: 'bold', color: '#486c53' }}>{project?.name}</Text></Text>
        </View>
        <View style={{ padding: 20, paddingBottom: '10px' }}>
          <Text style={{ fontSize: '14px' }}>Reference Year: <Text style={{ fontWeight: 'bold', color: '#486c53' }}>{year}</Text></Text>
        </View>
      </>
    )
  }

  const renderFinalCount = () => {
    let totalEmissions = 0;
    let totalYield = 0;
    let perTonneOfCrop = 0;
    let baseline = 247.6;

    selectedParcels.forEach(parcelId => {
      let assessment = assessments.find(assessment => assessment.parcel_id === parcelId && assessment.year.toString() === year.toString());
      totalEmissions += (assessment.fertilizers.totalEmission || 0) + (assessment.irrigation.totalEmission || 0) + (assessment.pesticides.totalEmission || 0) + (assessment.fuel.totalEmission || 0) + (assessment.residues.totalEmission || 0);
      totalYield += (parseFloat(assessment.yield) || 0);

    })
    totalYield = totalYield / 1000; // to tonnes

    perTonneOfCrop = totalEmissions / totalYield;

    let emissionReductionPerTonne = baseline - perTonneOfCrop;
    let total = (emissionReductionPerTonne * totalYield) / 1000;
    return (
      <View break style={{ ...styles.section, marginTop: '20px' }}>
        <Text style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '10px' }}>CARBON OFFSETS</Text>
        <Text style={{ fontSize: '14px', marginBottom: '10px' }}>Total Emissions: <Text style={{ fontWeight: 'bold', color: '#486c53' }}>{Math.round(totalEmissions)} kg CO2e</Text></Text>
        <Text style={{ fontSize: '14px', marginBottom: '10px' }}>Total Yield: <Text style={{ fontWeight: 'bold', color: '#486c53' }}>{Math.round(totalYield)} t</Text></Text>
        <Text style={{ fontSize: '14px', marginBottom: '10px' }}>Emissions per Tonne of Crop: <Text style={{ fontWeight: 'bold', color: '#486c53' }}>{perTonneOfCrop.toFixed(1)} kg CO2e/t</Text></Text>
        <Text style={{ fontSize: '14px', marginBottom: '10px' }}>Baseline Emissions per Tonne of Crop: <Text style={{ fontWeight: 'bold', color: '#486c53' }}>{baseline} kg CO2e/t</Text></Text>
        <Text style={{ fontSize: '14px', marginBottom: '10px' }}>Emission Reductions per Tonne of Crop: <Text style={{ fontWeight: 'bold', color: '#486c53' }}>{emissionReductionPerTonne.toFixed(1)} kg CO2e/t</Text></Text>
        <View style={{ padding: '10px', border: '1px solid #486c53', borderRadius: '8px' }}>
          <Text style={{ fontSize: '14px', fontWeight: 'bold', textAlign: 'center' }}>TOTAL CARBON OFFSETS: <Text style={{ fontWeight: 'bold', color: '#486c53' }}>{total.toFixed(1)} t CO2e</Text></Text>
        </View>
      </View>
    )

  }

  const renderParcelData = (parcel, i) => {
    let applicabilityLink = reports.find((report) => report.parcel === parcel.id && report.tag === 'applicability_report')?.doc;

    return (
      <View break={i > 0} key={parcel.id} style={{ paddingLeft: '20px', paddingRight: '20px' }}>
        <View style={{ borderBottom: '1px dashed gray', marginBottom: '10px', marginTop: '10px' }}>
        </View>
        <Text style={{ marginBottom: '10px', fontSize: '14px' }}>Field: <Text style={{ fontWeight: 'bold', color: '#486c53' }}>{parcel.name}</Text></Text>
        <Text style={{ marginBottom: '10px', fontSize: '14px' }}>Area: <Text style={{ fontWeight: 'bold', color: '#486c53' }}>{parcel.area} ha</Text></Text>
        <Text style={{ marginBottom: '10px', fontSize: '14px' }}>Crop: <Text style={{ fontWeight: 'bold', color: '#486c53' }}>{parcel.crop || 'Olives'}</Text></Text>
        <Text style={{ marginBottom: '10px', fontSize: '14px' }}>Applicability Report: <Link src={applicabilityLink}>Click Here</Link></Text>
        <View style={{ borderBottom: '1px dashed gray', marginBottom: '10px', marginTop: '10px' }}>
        </View>
      </View>
    )
  }

  const renderEmssionSources = (parcel) => {
    let assessment = assessments.find(assessment => assessment.parcel_id === parcel.id && assessment.year.toString() === year.toString());

    let docsList = documents.filter(doc => doc.parcel_id === parcel.id && doc.year.toString() === year.toString());

    let residues = (
      <View style={{ marginBottom: '10px', marginTop: '10px' }}>
        <Text style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', color: '#486c53' }}>Residues</Text>
        {assessment.residues.totalEmission > 0 ? assessment.residues.records.map((record, i) => (
          <Text key={i}>{record.amountN} | </Text>
        ))
          : <Text style={{ fontSize: '12px' }}>N/A</Text>}
      </View>
    );

    let pesticides = (
      <View style={{ marginBottom: '10px', marginTop: '10px' }}>
        <Text style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', color: '#486c53' }}>Plant Protection</Text>
        {assessment.pesticides.totalEmission > 0 ? assessment.pesticides.records.map((record, i) => (
          <Text style={{ fontSize: '12px', margin: '5px 0px' }} key={i}>{record.type} | {record.amountP} kg/ha | {record.date}</Text>
        ))
          : <Text style={{ fontSize: '12px' }}>N/A</Text>}
      </View>
    );

    let fertilizers = (
      <View style={{ marginBottom: '10px', marginTop: '10px' }}>
        <Text style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', color: '#486c53' }}>Fertilizers</Text>
        {assessment.fertilizers.totalEmission > 0 ? assessment.fertilizers.records.map((record, i) => (
          <Text style={{ fontSize: '12px', margin: '5px 0px' }} key={i}>{record.product}  ({record.percentageN > 0 ? (`N ${record.percentageN}%`) : `${record.percentageU}%`}) | {record.amount} kg/ha | {record.date}</Text>
        ))
          : <Text style={{ fontSize: '12px' }}>N/A</Text>}
      </View>
    );

    let animals = (
      <View style={{ marginBottom: '10px', marginTop: '10px' }}>
        <Text style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', color: '#486c53' }}>Animals</Text>
        {assessment.animals.totalEmission > 0 ? <Text style={{ fontSize: '12px', margin: '5px 0px' }}>{assessment.animals.product}  ({assessment.animals.percentageN > 0 ? (`N ${assessment.animals.percentageN}%`) : `${assessment.animals.percentageU}%`}) | {assessment.animals.amount} kg/ha | {assessment.animals.date}</Text>

          : <Text style={{ fontSize: '12px' }}>N/A</Text>}
      </View>
    );

    let fuel = (
      <View style={{ marginBottom: '10px', marginTop: '10px' }}>
        <Text style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', color: '#486c53' }}>Fuel</Text>
        {assessment.fuel.totalEmission > 0 ? (assessment.fuel.isDirect ? [...assessment.fuel.direct] : [...assessment.fuel.estimated]).map((record, i) => (
          <Text style={{ fontSize: '12px', margin: '5px 0px' }} key={i}>{record.source} | {record.fuel.name} | {record.amount} L</Text>
        ))
          : <Text style={{ fontSize: '12px' }}>N/A</Text>}
      </View>
    );

    let irrigation = (
      <View style={{ marginBottom: '10px', marginTop: '10px' }}>
        <Text style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', color: '#486c53' }}>Irrigation</Text>
        {assessment.irrigation.totalEmission > 0 ? assessment.irrigation.records.map((record, i) => (
          <Text style={{ fontSize: '12px', margin: '5px 0px' }} key={i}>{record.fuel} | {record.method} | {record.volume} m3</Text>
        ))
          : <Text style={{ fontSize: '12px' }}>N/A</Text>}
      </View>
    );

    let docs = (
      <View style={{ marginBottom: '10px', marginTop: '10px' }}>
        <Text style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '5px', color: '#486c53' }}>Documents Attached</Text>
        {docsList.length > 0 ? docsList.map((doc, i) => (
          <Link src={doc.file} style={{ fontSize: '12px', margin: '5px 0px' }} key={i}>{doc.name}</Link>
        ))
          : <Text style={{ fontSize: '12px' }}>No documents attached.</Text>}
      </View>
    );

    return <View style={{ padding: '20px' }}>
      <Text style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>EMISSION SOURCES</Text>
      {residues}
      {pesticides}
      {fertilizers}
      {animals}
      {fuel}
      {irrigation}
      {docs}
    </View>
  }

  const renderEmissions = (parcel) => {
    let assessment = assessments.find(assessment => assessment.parcel_id === parcel.id && assessment.year.toString() === year.toString());
    let totalEmissions = (assessment.fertilizers.totalEmission || 0) + (assessment.irrigation.totalEmission || 0) + (assessment.pesticides.totalEmission || 0) + (assessment.fuel.totalEmission || 0) + (assessment.residues.totalEmission || 0);

    let formatedTotal = totalEmissions.toFixed(2) + ' kg CO2e';
    return (
      <View style={{ padding: '20px' }}>
        <Text style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>EMISSIONS</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
          <View>
            <Text style={{ fontSize: '12px', fontWeight: 'normal', marginBottom: '10px' }}>Residues: <Text style={{ fontSize: '12px', color: '#486c53', fontWeight: 'bold' }}>{assessment.residues.totalEmission ? assessment.residues.totalEmission.toFixed(2) + ' kg CO2e' : ' - '}</Text> </Text>

            <Text style={{ fontSize: '12px', fontWeight: 'normal', marginBottom: '10px' }}>Plant Protection: <Text style={{ fontSize: '12px', color: '#486c53', fontWeight: 'bold' }}>{assessment.pesticides.totalEmission ? assessment.pesticides.totalEmission.toFixed(2) + ' kg CO2e' : ' - '}</Text></Text>

            <Text style={{ fontSize: '12px', fontWeight: 'normal', marginBottom: '10px' }}>Fertilizers: <Text style={{ fontSize: '12px', color: '#486c53', fontWeight: 'bold' }}>{assessment.fertilizers.totalEmission ? assessment.fertilizers.totalEmission.toFixed(2) + ' kg CO2e' : ' - '}</Text></Text>

            <Text style={{ fontSize: '12px', fontWeight: 'normal', marginBottom: '10px' }}>Animals: <Text style={{ fontSize: '12px', color: '#486c53', fontWeight: 'bold' }}>{assessment.animals.totalEmission ? assessment.animals.totalEmission.toFixed(2) + ' kg CO2e' : ' - '}</Text></Text>
            <Text style={{ fontSize: '12px', fontWeight: 'normal', marginBottom: '10px' }}>Fuel: <Text style={{ fontSize: '12px', color: '#486c53', fontWeight: 'bold' }}>{assessment.fuel.totalEmission ? assessment.fuel.totalEmission.toFixed(2) + ' kg CO2e' : ' - '}</Text></Text>

            <Text style={{ fontSize: '12px', fontWeight: 'normal', marginBottom: '10px' }}>Irrigation: <Text style={{ fontSize: '12px', color: '#486c53', fontWeight: 'bold' }}>{assessment.irrigation.totalEmission ? assessment.irrigation.totalEmission.toFixed(2) + ' kg CO2e' : ' - '}</Text></Text>
          </View>


          <View>
            <Text style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px' }}>Total Emissions</Text>
            <Text style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px', color: '#486c53' }}>{formatedTotal}</Text>
            <Text style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px', color: '#486c53' }}>{(totalEmissions / 1000).toFixed(2)} t CO2e</Text>

          </View>

        </View>
      </View>
    )
  }

  const renderBlock = (parcel, i) => {
    return (
      <>
        {renderParcelData(parcel, i)}

        {renderEmssionSources(parcel)}

        {renderEmissions(parcel)}
      </>
    )
  }

  const renderContent = () => {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <View style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            padding: 10,
            // height: '50px',
            textAlign: 'center',
            // The 'fixed' prop ensures this view appears on every page
          }} fixed render={({ pageNumber }) => {
            if (pageNumber > 1) {
              return (
                <View style={{ borderBottom: '1px solid gray', padding: '15px 15px 10px 15px', justifyContent: 'space-between', display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
                  <Text style={{ fontSize: '12px', color: 'gray' }}>FARMPACT</Text>
                  <Text style={{ fontSize: '12px', color: 'gray' }}>Report on GHG Emission Reductions</Text>
                </View>
              )
            }
            return null
          }} />
          {renderHeader()}

          {selectedParcels.map((parcelId, i) => {
            return renderBlock(parcels.find(p => p.id === parcelId), i);
          })}

          {renderFinalCount()}

          <View style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 10,

            textAlign: 'center',
            // The 'fixed' prop ensures this view appears on every page
          }} fixed render={({ pageNumber }) => {
            if (pageNumber > 1) {
              return (
                <View style={{ borderTop: '1px solid gray', paddingTop: 5 }}>
                  <Text style={{ fontSize: '12px', color: 'gray' }}>{pageNumber}</Text>
                </View>
              )
            }
            return null
          }} />

        </Page>
      </Document>
    )
  }

  const renderPdf = () => {
    return (
      <PDFViewer showToolbar={false} style={{ width: '100%', height: '800px' }}>
        {renderContent()}
      </PDFViewer>
    );
  };

  const renderParcels = () => {
    let filtered = parcels.filter((parcel) => {
      return compatibilityCheck(parcel);
    });

    return (
      <>
        <Typography>Only applicable parcels are shown. Please select which ones you want to include in the report.</Typography>
        <List dense>
          {
            filtered.map((parcel, i) => (
              <ListItem key={i}>

                <ListItemButton onClick={() => {
                  if (selectedParcels.includes(parcel.id)) {
                    setSelected(selectedParcels.filter(id => id !== parcel.id));
                  } else {
                    setSelected([...selectedParcels, parcel.id]);
                  }
                }}>
                  <ListItemIcon>
                    <Checkbox checked={selectedParcels.includes(parcel.id)} />
                  </ListItemIcon>
                  <ListItemText primary={parcel.name} secondary={`Area: ${parcel.area} ha, Crop: ${parcel.crop || 'Olives'}`} />
                </ListItemButton>
              </ListItem>
            ))
          }
        </List>
      </>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={(evt, reason) => {
        if (!loading.submit) {
          onClose();
        }
      }}
      maxWidth="md"
      fullScreen
    >
      <DialogTitle>
        Emissions Report
        <IconButton
          aria-label="close"
          onClick={(evt) => {
            if (!loading.submit) {
              onClose();
            }
          }}
          color="secondary"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {
          loading.assessments || loading.reports || loading.documents ? (
            <CircularProgress />
          ) : (
            renderParcels()
          )
        }

        {selectedParcels.length > 0 && renderPdf()}
      </DialogContent>

      <DialogActions>
        <Button disabled={loading.submit} onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={loading.submit || loading.assessments || loading.reports || loading.documents} onClick={getBlob} variant="contained">
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmissionReport;